import Bugsnag from '@bugsnag/js';
import { getUnixTime } from 'date-fns';
import { api } from './Api';
import { formatResponse } from '../utils/FormatUtils/formatResponse';

// Modes the deposit-reconciliation caller can find itself in. The "full" mode
// is what the report is designed for: rows filtered against the Stripe payout
// arrival date, with an envelope that splits Settled vs Estimated stripe fees.
// "transaction-date-fallback" is a degraded view we serve when the arrival-
// date variant 500s — the page collapses the Settled/Estimated split into a
// single Stripe Fee card and renders a warning banner so the user knows the
// numbers won't match the rest of the deposit-reconciliation contract.
export const STRIPE_RECON_MODE_FULL = 'full';
export const STRIPE_RECON_MODE_FALLBACK = 'transaction-date-fallback';

const isServerError = (status) =>
  typeof status === 'number' && status >= 500 && status < 600;

export const getOverview = async (id, { start, end }) => {
  const sales = await api.get(`/restmanager/${id}/report/sales`, {
    details: 1,
    interval: 'month',
    start: getUnixTime(start),
    end: getUnixTime(end),
    group: 'type',
  });
  const customers = await api.get(`/restmanager/${id}/report/customers`, {
    start: getUnixTime(start),
    end: getUnixTime(end),
  });
  const { config } = await api.get(`/restmanager/${id}/settings`);

  return formatResponse({
    data: {
      start,
      end,
      sales,
      customers,
      food_cost: config,
    },
    type: 'overview',
  });
};

export const getSalesTaxSummary = async (id, params) => {
  const response = await api.get(`/restmanager/${id}/report/sales/taxes`, {
    details: 1,
    interval: params.interval,
    sorting: 'desc',
    start: getUnixTime(params.start),
    end: getUnixTime(params.end),
  });

  return formatResponse({ data: { summary: response }, type: 'tax-summary' });
};

export const getSalesTaxTable = async ({ id, params, page }) => {
  const response = await api.get(`/restmanager/${id}/report/sales/taxes`, {
    details: 1,
    interval: params.interval,
    sorting: 'desc',
    start: getUnixTime(params.start),
    end: getUnixTime(params.end),
    page: Number(page),
  });

  return formatResponse({
    data: {
      table: { ...response, start: getUnixTime(params.start) },
    },
    type: 'tax-table',
  });
};

export const getStripeReconciliation = async (id, { start, end, page = 1 }) => {
  const response = await api.get(
    `/restmanager/${id}/report/reconciliation/stripe`,
    {
      start: getUnixTime(start),
      end: getUnixTime(end),
      details: 1,
      sorting: 'desc',
      page,
    }
  );
  return formatResponse({ data: response, type: 'stripe' });
};

export const getStripeReconciliationByPayout = async (
  id,
  { start, end, page = 1, signal, q, forceFallback = false }
) => {
  // Params shared by every variant of this request.
  const sharedParams = {
    start: getUnixTime(start),
    end: getUnixTime(end),
    details: 1,
    sorting: 'desc',
    page,
    ...(q ? { q } : {}),
  };

  // `deposits_scope=window` is only valid with date_field=settlement_arrival_date.
  // The backend enforces this constraint and returns 500 when it appears alongside
  // transaction_date. It must never be included in the fallback or forceFallback
  // paths — those always use transaction_date.
  const arrivalDateParams = {
    ...sharedParams,
    deposits_scope: 'window',
    date_field: 'settlement_arrival_date',
  };

  const txnDateParams = {
    ...sharedParams,
    date_field: 'transaction_date',
  };

  const endpoint = `/restmanager/${id}/report/reconciliation/stripe`;

  // When the caller already knows the arrival-date endpoint is degraded
  // (i.e. a previous request in this session fell back to transaction_date),
  // skip the doomed first attempt entirely and go straight to the fallback
  // endpoint. This halves latency for every subsequent page navigation
  // while the backend is in its degraded state.
  if (forceFallback) {
    const fallback = await api.get(endpoint, txnDateParams, {
      noAutoRetry: true,
      signal,
    });
    return {
      data: formatResponse({ data: fallback, type: 'stripe-payout' }),
      modeUsed: STRIPE_RECON_MODE_FALLBACK,
    };
  }

  // Single attempt at the full arrival-date view first. No auto-retry on
  // failure — the SPA does NOT silently re-issue the same request, because
  // backend has asked for deterministic traffic on this endpoint. The
  // `noAutoRetry: true` option disables Api.js's silent 403 refresh-and-
  // retry path so that even an auth blip surfaces immediately rather than
  // doubling up traffic against stripe-recon.
  try {
    const response = await api.get(endpoint, arrivalDateParams, {
      noAutoRetry: true,
      signal,
    });
    return {
      data: formatResponse({ data: response, type: 'stripe-payout' }),
      modeUsed: STRIPE_RECON_MODE_FULL,
    };
  } catch (err) {
    // 5xx only: arrival-date variant is the path that has been timing out
    // server-side. 4xx (validation, auth, etc.) is a real client problem
    // and must not be papered over with a fallback request.
    if (!isServerError(err?.status)) {
      throw err;
    }

    // Bugsnag breadcrumb (NOT notify) — Api.js already notified the original
    // 5xx as an event; the breadcrumb gives that event the context that we
    // recovered, plus the Ref id the user will see in the banner.
    try {
      Bugsnag.leaveBreadcrumb?.('stripe-recon transaction-date fallback', {
        from: 'settlement_arrival_date',
        to: 'transaction_date',
        status: err?.status,
        requestId: err?.requestId || null,
      });
    } catch (_) {
      // Never let instrumentation throw from the fallback path.
    }

    const fallback = await api.get(endpoint, txnDateParams, {
      noAutoRetry: true,
      signal,
    });
    return {
      data: formatResponse({ data: fallback, type: 'stripe-payout' }),
      modeUsed: STRIPE_RECON_MODE_FALLBACK,
    };
  }
};

export const getSalesTaxTableByIndex = async ({ id, params, page }) => {
  const response = await api.get(`/restmanager/${id}/report/sales/taxes`, {
    details: 1,
    interval: params.interval,
    sorting: 'desc',
    start: getUnixTime(params.start),
    end: getUnixTime(params.end),
    page: Number(page),
  });

  return formatResponse({
    data: {
      table: { ...response, start: getUnixTime(params.start) },
    },
    type: 'tax-table-by-index',
  });
};

// Per-order drill-down for the Sales Tax report. Returns each order's
// `order_id`, `transaction_date` (Unix), `tax_collected` and `jurisdiction`
// for the supplied [start, end) window. Always requested with `details=1`
// and `sorting=desc` so the most recent orders surface first; the API
// paginates at 20 rows per page.
export const getSalesTaxOrders = async (id, { start, end, page = 1 }) => {
  const response = await api.get(
    `/restmanager/${id}/report/sales/taxes/orders`,
    {
      details: 1,
      sorting: 'desc',
      start: getUnixTime(start),
      end: getUnixTime(end),
      page: Number(page),
    }
  );

  return {
    total_results: response.total_results || 0,
    total_pages: response.total_pages || 1,
    page: Number(response.page) || 1,
    results: (response.results || []).map((row) => ({
      orderId: row.order_id,
      transactionDate: row.transaction_date,
      taxCollected: row.tax_collected,
      jurisdiction: row.jurisdiction,
    })),
  };
};

export const getPOSReconciliation = async (id, { start, end, page = 1 }) => {
  const response = await api.get(
    `/restmanager/${id}/report/reconciliation/pos`,
    {
      start: getUnixTime(start),
      end: getUnixTime(end),
      details: 1,
      sorting: 'desc',
      page,
    }
  );

  return formatResponse({ data: response, type: 'pos' });
};

export const getSales = async (
  id,
  { start, end, interval = 'day', details = 0, group = 'type' },
  type
) => {
  const response = await api.get(`/restmanager/${id}/report/sales`, {
    details,
    interval,
    start: getUnixTime(start),
    end: getUnixTime(end),
    group,
  });

  return formatResponse({ data: { ...response, interval, start, end }, type });
};

export const getHighestSales = async (id, { start, end }, score, group) => {
  const response = await api.get(
    `/restmanager/${id}/report/sales/leaderboard`,
    {
      scope: 'item',
      start: getUnixTime(start),
      end: getUnixTime(end),
      group,
      limit: 10,
      score,
    }
  );

  return formatResponse({ data: response, type: 'highest-sale' });
};
